import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdDashboard,
    MdRefresh,
    MdSmartToy,
    MdSupportAgent,
} from 'react-icons/md'

interface DashboardHeaderProps {
    lastUpdated: string
    refreshing: boolean
    onRefresh: () => void
    humanResponseAvgSeconds?: number
    aiResponseAvgSeconds?: number
    humanResponseCount?: number
    aiResponseCount?: number
}

function formatDuration(totalSeconds: number): string {
    if (!totalSeconds || totalSeconds <= 0) return '—'
    if (totalSeconds < 60) return `${Math.round(totalSeconds)}s`
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = Math.round(totalSeconds % 60)
    if (minutes < 60) {
        return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
    }
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

export default function DashboardHeader({
    lastUpdated,
    refreshing,
    onRefresh,
    humanResponseAvgSeconds = 0,
    aiResponseAvgSeconds = 0,
    humanResponseCount = 0,
    aiResponseCount = 0,
}: DashboardHeaderProps) {
    const formattedDate = lastUpdated
        ? format(parseISO(lastUpdated), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
        : ''

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdDashboard className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Dashboard
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Visão geral do sistema
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <span className="text-sm text-base-content/50">
                        Atualizado: {formattedDate}
                    </span>
                    <button
                        onClick={onRefresh}
                        disabled={refreshing}
                        className="btn btn-sm btn-ghost gap-2"
                    >
                        <MdRefresh
                            className={`text-lg ${refreshing ? 'animate-spin' : ''}`}
                        />
                        Atualizar
                    </button>
                </div>
            </div>

            {/* Tempo medio de resposta */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-3 bg-base-100 border border-base-300/50 rounded-xl p-4 shadow-sm">
                    <div className="w-10 h-10 rounded-lg bg-info/10 flex items-center justify-center flex-shrink-0">
                        <MdSupportAgent className="text-xl text-info" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs text-base-content/60">
                            Tempo médio de resposta humana
                        </p>
                        <div className="flex items-baseline gap-2">
                            <span className="text-xl font-bold text-base-content">
                                {formatDuration(humanResponseAvgSeconds)}
                            </span>
                            <span className="text-xs text-base-content/50">
                                {humanResponseCount} resposta
                                {humanResponseCount === 1 ? '' : 's'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3 bg-base-100 border border-base-300/50 rounded-xl p-4 shadow-sm">
                    <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                        <MdSmartToy className="text-xl text-secondary" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs text-base-content/60">
                            Tempo médio de resposta da IA
                        </p>
                        <div className="flex items-baseline gap-2">
                            <span className="text-xl font-bold text-base-content">
                                {formatDuration(aiResponseAvgSeconds)}
                            </span>
                            <span className="text-xs text-base-content/50">
                                {aiResponseCount} resposta
                                {aiResponseCount === 1 ? '' : 's'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
